<template>
  <div class="visibility-dropdown">
    <button
      ref="buttonRef"
      class="btn-notification"
      :class="{ 'is-open': isOpen }"
      :disabled="disabled"
      @click="toggleDropdown"
    >
      <font-awesome-icon :icon="['fas', currentOption.icon]" class="btn-icon" />
      <span class="btn-text">{{ currentOption.label }}</span>
      <font-awesome-icon :icon="['fas', 'chevron-down']" class="btn-chevron" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="override-dropdown-menu"
        :style="dropdownStyles"
      >
        <div
          v-for="option in visibilityOptions"
          :key="option.value"
          class="frequency-option"
          :class="{ 'is-selected': visibility === option.value }"
          @click="selectVisibility(option.value)"
        >
          <span class="option-check-space">
            <font-awesome-icon v-if="visibility === option.value" :icon="['fas', 'check']" class="option-check" />
          </span>
          <font-awesome-icon :icon="['fas', option.icon]" class="option-icon" />
          <span class="option-label">{{ option.label }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  visibility: {
    type: Number,
    default: 0
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['change'])

const visibilityOptions = [
  { value: 0, label: 'Private', icon: 'eye-slash' },
  { value: 1, label: 'Organization', icon: 'building' },
  { value: 2, label: 'Public', icon: 'globe' }
]

const isOpen = ref(false)
const buttonRef = ref(null)
const dropdownStyles = ref({})

const currentOption = computed(() => {
  return visibilityOptions.find(o => o.value === props.visibility) || visibilityOptions[0]
})

function toggleDropdown() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    calculateDropdownPosition()
  }
}

function selectVisibility(value) {
  if (value !== props.visibility) {
    emit('change', value)
  }
  isOpen.value = false
}

function calculateDropdownPosition() {
  if (!buttonRef.value) return

  const rect = buttonRef.value.getBoundingClientRect()
  const dropdownHeight = 200
  const spaceBelow = window.innerHeight - rect.bottom

  if (spaceBelow >= dropdownHeight || spaceBelow > rect.top) {
    dropdownStyles.value = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  } else {
    dropdownStyles.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 8}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  }
}

function handleClickOutside(event) {
  if (isOpen.value && buttonRef.value && !buttonRef.value.contains(event.target)) {
    const dropdown = document.querySelector('.override-dropdown-menu')
    if (dropdown && !dropdown.contains(event.target)) {
      isOpen.value = false
    }
  }
}

function handleEscape(event) {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false
  }
}

function handleResize() {
  if (isOpen.value) {
    calculateDropdownPosition()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleResize)
})
</script>

<style scoped>
.visibility-dropdown {
  position: relative;
  display: inline-flex;
}

.btn-notification {
  padding: 0.625rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #9CA3AF;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-notification:hover:not(:disabled) {
  background: rgba(139, 92, 246, 0.12);
  border-color: rgba(139, 92, 246, 0.25);
  color: #E5E7EB;
}

.btn-notification.is-open {
  background: rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.35);
}

.btn-notification:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 0.875rem;
  flex-shrink: 0;
  width: 1rem;
  text-align: center;
}

.btn-text {
  white-space: nowrap;
}

.btn-chevron {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 0.125rem;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.btn-notification.is-open .btn-chevron {
  transform: rotate(180deg);
}

/* Dropdown menu */
.override-dropdown-menu {
  background: #1a1f28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.625rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  z-index: 9999;
  overflow: hidden;
  padding: 0.5rem 0;
}

.frequency-option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.frequency-option:hover {
  background: rgba(255, 255, 255, 0.04);
}

.frequency-option.is-selected {
  background: rgba(139, 92, 246, 0.08);
}

.frequency-option.is-selected:hover {
  background: rgba(139, 92, 246, 0.12);
}

.option-check-space {
  width: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.option-check {
  font-size: 0.75rem;
  color: #a78bfa;
}

.option-icon {
  font-size: 0.8125rem;
  color: #9CA3AF;
  width: 1rem;
  text-align: center;
}

.frequency-option.is-selected .option-icon {
  color: #a78bfa;
}

.option-label {
  font-size: 0.875rem;
  color: #E5E7EB;
}
</style>
